import MUIAvatar, { AvatarProps } from '@mui/material/Avatar';
import MUIAvatarGroup, { AvatarGroupProps } from '@mui/material/AvatarGroup';
import MUIBadge, { BadgeProps } from '@mui/material/Badge';
import styled, { useTheme } from 'styled-components';
import { BusinessCardHighlight } from '../business-card';
import { Skeleton } from '../skeleton';

interface MuiAvatarProps extends AvatarProps {
	size?: 'xsmall' | 'small' | 'medium' | 'large';
	fontColor?: string;
	loading?: boolean;
}

const SIZES = {
	xsmall: { height: '1.125rem', width: '1.125rem' },
	small: { height: '2.5rem', width: '2.5rem' },
	medium: { height: '3.25rem', width: '3.25rem' },
	large: { height: '4.25rem', width: '4.25rem' }
};

const RIGHT_OFFSETS = {
	xsmall: '-0.9375rem',
	small: '-1.25rem',
	medium: '-1.59375rem',
	large: '-2.09375rem'
};

export const Avatar = ({ size = 'small', fontColor, sx, loading, ...props }: MuiAvatarProps) => {
	const sizeStyle = SIZES[size];
	const theme = useTheme();
	const backgroundColor = props.src ? theme.palette.common.white : theme.palette.grey[600];
	const textColor = fontColor ?? theme.palette.getContrastText(backgroundColor);

	const businessCardStyles = {
		[`${BusinessCardHighlight} &`]: {
			position: 'absolute',
			top: '1.25rem',
			right: RIGHT_OFFSETS[size]
		}
	};

	return loading ? (
		<Skeleton variant="circular" style={{ ...sizeStyle, ...businessCardStyles }} />
	) : (
		<MUIAvatar {...props} variant="circular" sx={{ ...sizeStyle, backgroundColor, color: textColor, ...businessCardStyles, ...sx }} />
	);
};

export const AvatarGroup = (props: AvatarGroupProps) => {
	return <MUIAvatarGroup {...props} variant="circular" />;
};

/**
 * !!! EXCEPTION CASE !!!!
 * We want all our badges be styled same across all the platform.
 * MuiStyledBadge - used for representing Icon with Numbers
 * ex: ( mail and number in case if there are any mails).
 */
export const MuiBadge = (props: BadgeProps) => {
	return <MUIBadge {...props} />;
};

const BigStyledBadge = styled(MuiBadge)(({ theme }) => ({
	'& .MuiBadge-badge': {
		right: -3,
		top: 5,
		border: `1px solid ${theme.palette.background.paper}`,
		padding: '0 4px',
		fontSize: '0.7rem'
	}
}));

const MediumStyledBadge = styled(MuiBadge)(({ theme }) => ({
	'& .MuiBadge-badge': {
		right: -3,
		top: 5,
		border: `1px solid ${theme.palette.background.paper}`,
		padding: '0 4px ',
		fontSize: '0.6rem'
	}
}));

const SmallStyledBadge = styled(MuiBadge)(({ theme }) => ({
	'& .MuiBadge-badge': {
		right: -3,
		top: 5,
		border: `1px solid ${theme.palette.background.paper}`,
		padding: '0 3px',
		fontSize: '0.5rem'
	}
}));

export const MuiStyledBadge = (props: BadgeProps) => {
	if (typeof props.badgeContent === 'number' && props.badgeContent > 99) {
		return <SmallStyledBadge {...props} />;
	}
	if (typeof props.badgeContent === 'number' && props.badgeContent > 9) {
		return <MediumStyledBadge {...props} />;
	}
	return <BigStyledBadge {...props} />;
};

export const ExpandedBetaNavBadge = styled(MuiBadge)(() => ({
	'& .MuiBadge-badge': {
		right: -22,
		top: 2
	}
}));

export const BetaNavBadge = styled(MuiBadge)(() => ({
	'& .MuiBadge-badge': {
		right: 1,
		top: 1
	}
}));

export const BetaBadge = styled(MuiBadge)(({ theme }) => ({
	'& .MuiBadge-badge': {
		border: `1px solid ${theme.colors.secondary}`,
		padding: '12px',
		fontSize: '1rem'
	}
}));
